<template>
  <div class="breadcrumb-for-listing marker--breadcrumb-for-listing">
    <breadcrumb-listing-picker
      :title="title"
      :selected-id="selectedEntityId"
      :icon-name="iconName"
      :all-conversations-selected="isAllConversations"
      :all-tasks-selected="isAllTasks"
      :is-mailbox="entityType === EEntities.MAILBOXES && selectedEntityId !== null"
    />
    <i :class="arrowClasses" />
    <div v-if="$screen.isDesktop($mq) && showNameForm" class="name-form">
      <input
        ref="inputRef"
        v-model="newName"
        class="filter-name-input"
        @keydown.enter.prevent="upsertFilter()"
      >
      <dd-button
        :is-disabled="newNameIsEmpty"
        :is-loading="isCustomFilterSubmitting"
        size="tiny"
        type="flat"
        class="save-filter-button"
        @click="upsertFilter()"
      >Save
      </dd-button>
      <i class="ddi ddi-close" @click="userWantsToChangeName=false" />
    </div>
    <breadcrumb-filter-picker
      v-else
      :default-filters="defaultFilters"
      :custom-filters="customFilters"
      :current-filter="currentFilter"
      :total-item-count="totalItemCount"
      :active-assignee-counts="activeAssigneeCounts"
      :deemphasized="isUnnamedFilter"
      :allow-filter-delete="$screen.isTabletOrDesktop($mq)"
      @change-filter="(val) => $emit('change-filter', val)"
      @delete-custom-filter="(val) => $emit('delete-custom-filter', val)"
      @select-assignee="(val) => $emit('select-assignee', val)"
    />
    <template v-if="$screen.isDesktop($mq) && !showNameForm">
      <dd-button
        v-if="isUnnamedFilter"
        type="text"
        class="save-as-filter-button"
        @click="userWantsToChangeName=true"
      >Save as…
      </dd-button>
      <div v-else-if="isCustomFilter" class="edit-filter-buttons">
        <i class="ddi ddi-edit" title="Edit name" @click="userWantsToChangeName=true" />
        <i class="ddi ddi-trash" title="Remove" @click="$emit('delete-current-custom-filter')" />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent, PropType } from 'vue'
import {
  ContentPlaceholders,
  ContentPlaceholdersText,
} from '@/components/global/content-placeholders'
import {
  IBreadcrumbDataFromListing,
  INavMenuItem,
} from '@/types/data-objects/global'

/* Mixins */

/* Components */

/* Helpers */
import ItemFilterAssistant from '@/helpers/item-filter-assistant'
import TextAssistant from '@/helpers/text-assistant'
import BreadcrumbAssistant from '@/helpers/breadcrumb-assistant'

/* Data types */
import { EEntities } from '@/types'

/* API */

export default defineComponent({
  name: 'BreadcrumbForListing',

  components: {
    ContentPlaceholders,
    ContentPlaceholdersText,
  },

  props: {
    isCustomFilterSubmitting: {
      type: Boolean,
      default: false,
    },
    entityType: {
      type: String as () => EEntities,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    selectedEntityId: {
      type: Number,
      default: null,
    },
    isKanban: {
      type: Boolean,
      default: false,
    },
    totalItemCount: {
      type: Number,
      default: null,
    },
    defaultFilters: {
      type: Array as () => INavMenuItem[],
      required: true,
    },
    customFilters: {
      type: Array as () => INavMenuItem[],
      required: true,
    },
    currentFilter: {
      type: Object as () => INavMenuItem,
      required: true,
    },
    activeAssigneeCounts: {
      type: Array as PropType<{ id: number; name: string; count: number }[]>,
      default: (): any[] => [],
    },
  },

  emits: [
    'change-filter',
    'delete-current-custom-filter',
    'delete-custom-filter',
    'save-new-filter',
    'select-assignee',
    'update-custom-filter-name',
  ],

  data() {
    return {
      EEntities,
      ItemFilterAssistant,
      userWantsToChangeName: false,
      newName: '',
    }
  },
  computed: {
    isAllConversations(): boolean {
      return (
        this.entityType === EEntities.MAILBOXES &&
        this.selectedEntityId === null
      )
    },
    isAllTasks(): boolean {
      return (
        this.entityType === EEntities.PROJECTS && this.selectedEntityId === null
      )
    },
    newNameIsEmpty(): boolean {
      return this.newName.trim() === ''
    },
    showNameForm(): boolean {
      return this.userWantsToChangeName && this.renameable
    },
    renameable(): boolean {
      return this.isUnnamedFilter || this.isCustomFilter
    },
    isCustomFilter(): boolean {
      return ItemFilterAssistant.isCustomIdentifier(this.currentFilter.value)
    },
    iconName(): string {
      if (this.isAllConversations) {
        return TextAssistant.itemSingularName(EEntities.MAILBOXES).toLowerCase()
      }
      if (this.isAllTasks) {
        return TextAssistant.itemSingularName(EEntities.PROJECTS).toLowerCase()
      }
      return TextAssistant.entitySingularName(this.entityType).toLowerCase()
    },
    arrowClasses(): string {
      if (this.$screen.isDesktop(this.$mq)) {
        return 'ddi ddi-caret-right'
      }
      return 'hidden'
    },
    isUnnamedFilter(): boolean {
      return (
        this.currentFilter.value ===
        ItemFilterAssistant.getUnnamedFilterIdentifier()
      )
    },
  },

  watch: {
    /* These are the only two things that would change after the initial
       load of the project/mailbox home, so watch them to keep the cached
       filter state synced after the component load. */
    /* This is loaded async, so refresh breadcrumb data cache as needed */
    totalItemCount() {
      this.cacheBreadcrumbDataFromListing()
    },
    currentFilter() {
      this.cacheBreadcrumbDataFromListing()
    },
    showNameForm: {
      handler(show) {
        if (show) {
          if (this.isCustomFilter) {
            this.newName = this.currentFilter.label
          } else if (this.isUnnamedFilter) {
            this.newName = ''
          }
          this.$nextTick(() => {
            // @ts-ignore
            this.$refs.inputRef.focus()
          })
        } else {
          this.userWantsToChangeName = false
        }
      },
    },
    isCustomFilterSubmitting: {
      handler(val) {
        if (!val) {
          // If a submit has finished, "close" the name form
          this.userWantsToChangeName = false
        }
      },
    },
  },

  created() {
    this.cacheBreadcrumbDataFromListing()
  },

  methods: {
    cacheBreadcrumbDataFromListing() {
      const data = {
        title: this.title,
        iconName: this.iconName,
        entityType: this.entityType,
        selectedEntityID: this.selectedEntityId,
        totalItemCount: this.totalItemCount,
        defaultFilters: this.defaultFilters,
        customFilters: this.customFilters,
        currentFilter: this.currentFilter,
      } as IBreadcrumbDataFromListing

      BreadcrumbAssistant.cacheBreadcrumbDataFromListing(data)
    },
    upsertFilter() {
      if (this.isUnnamedFilter) {
        this.$emit('save-new-filter', this.newName)
      } else if (this.isCustomFilter) {
        this.$emit('update-custom-filter-name', this.newName)
      }
    },
  },
})
</script>

<style lang="scss">
.breadcrumb-for-listing {
  @include single-line-ellipsis;
  display: flex;
  align-items: center;
  padding-right: 10px;

  > .ddi-caret-right {
    color: var(--color-text-primary);
    font-size: 8px;
    padding: 0 14px;
  }

  > .hidden {
    display: none;
  }

  .name-form {
    display: flex;
    align-items: center;

    @include flex-row-horizontal-spacing(10px);

    > .ddi-close {
      font-size: 12px;
      @include clickable-gray-color-states;
    }
  }

  .filter-name-input {
    width: 100%;
    background: transparent;
    border: transparent;
    border-bottom: $border--solid-light;
    width: 240px;
    max-width: 100%;
    outline: none;
    @include lettering--p;
  }

  .save-as-filter-button {
    margin-left: 12px;
    color: $color--blue;
    text-decoration: underline;
    height: auto;
    padding: 0;
  }

  .edit-filter-buttons {
    display: flex;
    align-items: center;
    margin-left: 6px;

    > .ddi {
      margin-left: 10px;
      @include clickable-gray-color-states;
    }
  }

  @include media-breakpoint-down(md) {
    flex-wrap: wrap;
    gap: 4px 10px;
    width: 100%;
    padding: 2px 0;

    > .ddi-caret-right {
      display: none;
    }
  }
}
</style>
